interface Point {
	top: number;
	bottom: number;
	left: number;
}

interface Box extends Point {
	width: number;
	height: number;
}

/**
 * Clamp a number between 0 and `max`
 */
function clamp(n: number, max: number): number {
	return Math.max(0, Math.min(n, max));
}

/**
 * Get a position directly above `at`, within the bounds of `bounds`. This
 * means that, based on the width of `thing`, the returned values might not be
 * directly above `at`, if `thing` were to go outside of `bounds`.
 *
 * @param thing - The object that is being positioned
 * @param at - The object that `thing` should be positioned above
 * @param bounds - The container that `thing` should not leave
 * @returns The left and top values that should be applied to `thing`, as well
 * as `isBeneath`, which is true when `thing` will be rendered below `at`.
 */
export function position(thing: Box, at: Point, bounds: Box): { left: number; top: number; isBeneath: boolean } {
	let left, top, isBeneath;

	// `left` is initially the position of `at` relative to `bounds`
	left = at.left - bounds.left;
	// Prevent `thing` from going off the left or right side of `bounds`
	left = clamp(left - thing.width / 2, bounds.width - thing.width);

	// If the `thing` is taller than the amount of room on top, position it on
	// the bottom.
	isBeneath = thing.height > at.top - bounds.top;
	if (isBeneath) {
		// Here we only limit the position to the end of the `bounds`, i.e. we
		// don't leave room for the `thing` necessarily. This can mean that it will
		// extend past the bottom of the `bounds`, but the alternative is that we
		// cover the `at` which kinda sucks. Note that we have already determined
		// that the `thing` doesn't fit above `at`.
		top = clamp(at.bottom - bounds.top, bounds.bottom - bounds.top);
	} else {
		// `top` is the top of `at` relative to `bounds`, minus the height of
		// `thing` so that `thing` is positioned above `at` on the Y axis.
		top = clamp(at.top - bounds.top - thing.height, bounds.height);
	}

	return { left, top, isBeneath };
}

/**
 * Simplified version of `position` that doesn't take into account whether
 * `thing` will fit above `at`. This is useful when you want to position
 * something above `at` but don't care if it goes off the top of the bounding box.
 */
export function simplePosition(thing: Box, at: Point, bounds: Box, marginTop = 0): { left: number; top: number } {
	let left, top;

	left = at.left - bounds.left;
	left = clamp(left - thing.width / 2, bounds.width - thing.width);
	top = at.top - bounds.top - thing.height;
	if (top + marginTop < 0) {
		top = at.bottom - bounds.top - 2 * marginTop;
	}

	return { left, top };
}
